import React, { useEffect } from 'react';
import { X, Upload, Link, Camera } from 'lucide-react';

const WorkflowSelector = ({ isOpen, onClose, onSelectManual, onSelectURL, onSelectAI }) => {
  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-charcoal/60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="p-6 border-b border-beige flex items-center justify-between">
          <h2 className="text-2xl font-light text-charcoal">Add to Stash</h2>
          <button
            onClick={onClose}
            className="text-gray-soft hover:text-charcoal transition-colors duration-200"
            data-testid="close-workflow-selector"
          >
            <X size={24} />
          </button>
        </div>

        {/* Workflow Options */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-soft text-center mb-2">How would you like to add your item?</p>

          {/* Snap a Photo (AI) */}
          <button
            onClick={onSelectAI}
            className="w-full flex items-center gap-4 p-5 rounded-xl border-2 border-sage bg-sage/5 hover:bg-sage/10 transition-all duration-200 text-left group relative overflow-hidden"
            data-testid="workflow-ai-btn"
          >
            <div className="w-14 h-14 rounded-full bg-sage/20 flex items-center justify-center flex-shrink-0 group-hover:bg-sage/30 transition-colors">
              <Camera size={24} className="text-sage" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-medium text-charcoal">Snap a Photo</h3>
                <span className="text-[10px] font-semibold px-2 py-0.5 bg-sage text-white rounded-full uppercase tracking-wider">Smart</span>
              </div>
              <p className="text-sm text-gray-soft mt-0.5">
                Snap a photo and let Smart Search identify your product
              </p>
            </div>
          </button>

          {/* URL Lookup */}
          <button
            onClick={onSelectURL}
            className="w-full flex items-center gap-4 p-5 rounded-xl border-2 border-beige hover:border-sage hover:bg-sage/5 transition-all duration-200 text-left group"
            data-testid="workflow-url-btn"
          >
            <div className="w-14 h-14 rounded-full bg-cream flex items-center justify-center flex-shrink-0 group-hover:bg-sage/20 transition-colors">
              <Link size={24} className="text-sage" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-medium text-charcoal">Add by URL</h3>
              <p className="text-sm text-gray-soft mt-0.5">
                Paste a product page or image link to auto-fill details
              </p>
            </div>
          </button>

          {/* Manual Entry */}
          <button
            onClick={onSelectManual}
            className="w-full flex items-center gap-4 p-5 rounded-xl border-2 border-beige hover:border-sage hover:bg-sage/5 transition-all duration-200 text-left group"
            data-testid="workflow-manual-btn"
          >
            <div className="w-14 h-14 rounded-full bg-cream flex items-center justify-center flex-shrink-0 group-hover:bg-sage/20 transition-colors">
              <Upload size={24} className="text-sage" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-medium text-charcoal">Manual Entry</h3>
              <p className="text-sm text-gray-soft mt-0.5">
                Type in product details and upload a photo
              </p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowSelector;
